import type { Area, Issue } from './types';
import { ORIGINAL_TYPES, liveTypes, meansOf, type IssueType } from './issue-types';

/**
 * WHAT THE BOARD IS MADE OF, COUNTED BY AREA — #269.
 *
 * The question this exists to answer is the one a customer asks first and the
 * board could not answer at all: not "how much work is there" but "what sort of
 * work, and where". Type alone says whether we are fixing or building; area
 * alone says which part is busy. Only the two together say that the UI is
 * mostly improvements while the back-end is mostly bugs — which is a decision,
 * where either number on its own is a statistic.
 */
export interface AreaRow {
  id: string;
  name: string;
  colour: string;
  retired: boolean;
  total: number;
  /** Counted by the board's own type slugs — #302. */
  byKind: Record<string, number>;
  /** And by what those types COUNT AS, which is what a chart reads. */
  fixing: number;
}

/** The id used for cards with no area. Not a real area and never stored. */
export const NO_AREA = '';

/**
 * WHAT "SHOW ME THE ONES WITH NO AREA" LOOKS LIKE AS A FILTER.
 *
 * It cannot be `''`, because in the filter `''` already means "every area" —
 * so clicking the "No area" row on Insights set a filter that meant no filter,
 * navigated to the Backlog, and showed everything. It did not fail; it did
 * something plausible and wrong, which is worse.
 *
 * A word rather than a uuid, and areas are uuids, so it cannot collide with one.
 */
export const FILTER_NO_AREA = 'none';

/**
 * One row per area, plus one for the cards that have none.
 *
 * "NO AREA" IS A ROW, NOT A GAP. On this board 244 of 300 cards have no area
 * and nobody is going back over them. Leaving them out would make the chart
 * describe a fifth of the work as though it were all of it — and the totals
 * would stop matching the number at the top of the screen, which is how a
 * report loses somebody's trust for good.
 *
 * RETIRED AREAS STILL COUNT, marked as retired. Vinnie's decision of 26 August
 * 2026: a six-month chart must not change shape because somebody tidied the
 * settings this morning.
 */
export function byArea(
  issues: Issue[],
  areas: Area[],
  /* The board's own types — #302. Defaulted to the four originals so every
     caller and check that predates this keeps working unchanged. */
  types: IssueType[] = ORIGINAL_TYPES
): AreaRow[] {
  /* THE BOARD'S OWN TYPES — #302, in the board's order. Counting into the
     four fixed words would leave every column empty on a board that renamed
     them, and the report would read as though nothing had been classified. */
  const blank = () =>
    liveTypes(types).reduce((a, t) => ({ ...a, [t.slug]: 0 }), {} as Record<string, number>);
  const rows = new Map<string, AreaRow>();
  for (const a of areas)
    rows.set(a.id, {
      id: a.id, name: a.name, colour: a.colour,
      retired: Boolean(a.retiredAt), total: 0, byKind: blank(), fixing: 0,
    });
  rows.set(NO_AREA, {
    id: NO_AREA, name: 'No area', colour: '#8b8b96',
    retired: false, total: 0, byKind: blank(), fixing: 0,
  });

  for (const i of issues) {
    /*
     * An area on a card that is no longer on the board counts as "No area"
     * rather than being dropped. It should not happen — nothing deletes an area
     * — but if it ever does, the totals still add up and the discrepancy shows
     * as a bigger "No area" instead of as work that quietly vanished.
     */
    const row = rows.get(i.area ?? NO_AREA) ?? rows.get(NO_AREA)!;
    row.total += 1;
    row.byKind[i.kind] = (row.byKind[i.kind] ?? 0) + 1;
    /* ⚠️ COUNTED BY MEANING, not by the slug `bug` — the whole of #302 in one
       line. A board calling it "Defect" used to score zero here. */
    if (meansOf(i.kind, types) === 'fixing') row.fixing++;
  }

  const live = [...rows.values()].filter((r) => r.id !== NO_AREA && (r.total > 0 || !r.retired));
  live.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  const none = rows.get(NO_AREA)!;
  /* Always last, however big it is: it is the absence of an answer, not the
     busiest part of the product, and sorting it to the top would say otherwise. */
  return none.total > 0 ? [...live, none] : live;
}

/**
 * What share of an area's work is fixing rather than building, 0–1.
 *
 * ⚠️ READ OFF THE MEANING, NOT OFF `byKind.bug` — #302. A board whose fixing
 * types are "Defect" and "Incident" scored a flat zero here and the bar looked
 * like an area with nothing wrong in it, which is the most misleading thing
 * this report could have said.
 */
export function bugShare(row: AreaRow): number {
  return row.total === 0 ? 0 : row.fixing / row.total;
}
